"use client";

import { useState, useEffect, useRef } from "react";
import { ShoppingBag } from "lucide-react";
import FallbackImage from "@/components/ui/FallbackImage";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { formatPrice } from "@/lib/utils";
import { useCart } from "@/context/CartContext";
import { trackEvent } from "@/lib/track";

interface StickyAddToCartProps {
  productName: string;
  price: number;
  comparePrice?: number | null;
  productId: string;
  image?: string;
  inStock: boolean;
  stockCount?: number | null;
}

export default function StickyAddToCart({
  productName,
  price,
  comparePrice,
  productId,
  image,
  inStock,
  stockCount,
}: StickyAddToCartProps) {
  const [isVisible, setIsVisible] = useState(false);
  const { addItem } = useCart();
  const t = useTranslations("product");
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Find the main Add to Cart button on the page
    const mainButton = document.querySelector("[data-add-to-cart-main]");
    if (!mainButton) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        // Show sticky bar when the main button scrolls out of view
        setIsVisible(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observerRef.current.observe(mainButton);

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  const handleAddToCart = () => {
    trackEvent("sticky_add_to_cart", { productId, productName, price });
    addItem({
      productId,
      name: productName,
      price,
      quantity: 1,
      image,
    });
  };

  const isLowStock = stockCount != null && stockCount > 0 && stockCount <= 5;
  const hasDiscount = comparePrice != null && comparePrice > price;

  return (
    <div
      className={cn(
        "fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-brand-dark/8 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] pb-[max(0.75rem,env(safe-area-inset-bottom))]",
        "md:hidden",
        "transform transition-all duration-500 ease-out",
        isVisible ? "translate-y-0" : "translate-y-full"
      )}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        {/* Product thumbnail + info */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Thumbnail */}
          <div className="relative h-10 w-10 flex-shrink-0 rounded-sm overflow-hidden bg-brand-cream">
            {image ? (
              <FallbackImage
                src={image}
                alt={productName}
                fill
                sizes="40px"
                className="object-cover"
                fallbackClassName="h-full w-full flex items-center justify-center bg-brand-cream"
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center">
                <ShoppingBag className="h-4 w-4 text-brand-gold/30" />
              </div>
            )}
          </div>

          {/* Name + price */}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-brand-dark truncate">
              {productName}
            </p>
            <div className="flex items-center gap-2">
              <span className={cn(
                "font-display text-sm",
                hasDiscount ? "text-brand-dark" : "text-brand-gold"
              )}>
                {formatPrice(price)}
              </span>
              {hasDiscount && (
                <span className="text-xs text-brand-muted/70 line-through">
                  {formatPrice(comparePrice)}
                </span>
              )}
              {isLowStock && (
                <span className="text-[11px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-sm whitespace-nowrap">
                  {t("lowStockShort", { count: stockCount })}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Add to Cart button */}
        <button
          onClick={handleAddToCart}
          disabled={!inStock}
          className={cn(
            "flex items-center gap-2 px-5 py-2.5 min-h-[44px] text-[11px] font-medium tracking-[0.15em] uppercase rounded-sm",
            "transition-all duration-500 whitespace-nowrap flex-shrink-0",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50 focus-visible:ring-offset-2",
            inStock
              ? "bg-brand-dark text-white shadow-md hover:bg-brand-gold hover:shadow-lg active:scale-[0.97]"
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
          )}
        >
          <ShoppingBag className="h-4 w-4" />
          {inStock ? t("addToCart") : t("soldOut")}
        </button>
      </div>
    </div>
  );
}
